import { useEffect, useState } from 'react'
import { isPreloadSettled, preloadImage } from '@/utils/imageCache'

// Product thumbnails mount their <img> only once its bitmap is decoded in the shared cache. A
// thumbnail that loads while the card's entrance animation is compositing can otherwise finish
// decoding without the browser repainting the (already laid out) image box — it stays blank until
// some unrelated invalidation, e.g. a hover, forces a repaint. Inserting a new node always paints.
//
// The hook remembers its own finished preload rather than trusting the cache alone: the cache is
// wiped with the query cache, and a wipe mid-download would otherwise leave the frame empty for
// good. It also reports ready after IMAGE_WAIT_MS, so a stalled download or decode can't either.
const IMAGE_WAIT_MS = 8000

export function useDecodedImage(src: string): boolean {
  const [readySrc, setReadySrc] = useState<string | null>(null)

  useEffect(() => {
    if (isPreloadSettled(src)) {
      return
    }
    let active = true
    const timer = setTimeout(() => {
      if (active) {
        setReadySrc(src)
      }
    }, IMAGE_WAIT_MS)
    const load = async () => {
      await preloadImage(src)
      if (active) {
        setReadySrc(src)
      }
    }
    load()
    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [src])

  return readySrc === src || isPreloadSettled(src)
}
